import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { supabase } from '@/shared/lib/supabase'
import { friendlyAuthError } from '@/shared/lib/errors'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'

export function Login() {
  const navigate = useNavigate()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [sessionExpired, setSessionExpired] = useState(false)
  const honeypotRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (sessionStorage.getItem('shelvi_session_expired')) {
      setSessionExpired(true)
      sessionStorage.removeItem('shelvi_session_expired')
    }
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate('/dashboard', { replace: true })
    })
  }, [navigate])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (honeypotRef.current?.value) return
    setError(null)
    setLoading(true)

    const email = identifier.trim()

    if (!email.includes('@')) {
      // Username login — resolved server-side, never exposes the email mapping.
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/auth/username-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: email.toLowerCase(), password }),
        })
        if (!res.ok) {
          setLoading(false)
          setError(res.status === 429 ? 'Too many attempts. Please wait a moment.' : 'Invalid credentials')
          return
        }
        const tokens: { access_token: string; refresh_token: string } = await res.json()
        const { error: sessionError } = await supabase.auth.setSession(tokens)
        setLoading(false)
        if (sessionError) {
          setError(friendlyAuthError(sessionError))
        } else {
          navigate('/dashboard', { replace: true })
        }
      } catch {
        setLoading(false)
        setError('Could not reach the server. Check your connection and try again.')
      }
      return
    }

    const { error } = await supabase.auth.signInWithPassword({ email, password })
    setLoading(false)
    if (error) {
      setError(friendlyAuthError(error))
    } else {
      navigate('/dashboard', { replace: true })
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'oklch(0.969 0.03 75)' }}>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <span className="font-heading text-3xl font-bold text-primary tracking-tight">Shelvi</span>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">Welcome back</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to your account</p>
        </div>

        {sessionExpired && (
          <div className="mb-4 rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
            Your session expired. Please sign in again.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
            <input
              ref={honeypotRef}
              type="text"
              name="phone_number"
              tabIndex={-1}
              aria-hidden="true"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium" htmlFor="identifier">
              Email or username
            </label>
            <Input
              id="identifier"
              type="text"
              placeholder="you@example.com or your_username"
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              required
              autoComplete="username"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium" htmlFor="password">
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground underline-offset-4 hover:underline hover:text-foreground"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button type="submit" className="w-full" size="lg" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          No account yet?{' '}
          <Link to="/signup" className="font-medium text-foreground underline-offset-4 hover:underline">
            Create one
          </Link>
        </p>
      </div>
    </div>
  )
}
